import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { router } from "@inertiajs/react";
import { MessageCircle } from "lucide-react";
import { useState } from "react";
import { TrackEventCard } from "./TrackEventCard";
import type RaceWeekend from "@/types/generated/RaceWeekend";
import type { Comment, IndividualEvent } from "@/types/generated";

type IndividualEventWithComments = IndividualEvent & {
  comments?: Comment[];
};
import { CommentInput } from "./CommentInput";
import { CommentsList } from "./CommentsList";
import RaceWeekendLikeButton from "./RaceWeekendLikeButton";

interface RaceWeekendCardProps {
  raceWeekend: RaceWeekend;
}

export function RaceWeekendCard({ raceWeekend }: RaceWeekendCardProps) {
  const [showCommentInput, setShowCommentInput] = useState(false);

  const getSessionTypeOrder = (sessionType: string) => {
    switch (sessionType) {
      case "practice":
        return 1;
      case "qualifying":
        return 2;
      case "race":
        return 3;
      default:
        return 4;
    }
  };

  const sortedSessions = [...raceWeekend.sessions].sort((a, b) => {
    const orderA = getSessionTypeOrder(a.session_type);
    const orderB = getSessionTypeOrder(b.session_type);
    if (orderA !== orderB) return orderA - orderB;
    return (
      new Date(a.session_started_at || new Date()).getTime() -
      new Date(b.session_started_at || new Date()).getTime()
    );
  });

  // Transform TrackEvent sessions for TrackEventCard
  const individualEvents: IndividualEventWithComments[] = sortedSessions.map((session) => ({
    type: "individual" as const,
    ...session,
    comments: [], // Sessions don't have individual comments, they're on the weekend
  }));

  const userName = `${raceWeekend.user.first_name} ${raceWeekend.user.last_name}`;
  const timestamp = raceWeekend.earliest_session_time
    ? formatDistanceToNow(new Date(raceWeekend.earliest_session_time), {
        addSuffix: true,
      })
    : null;

  const raceWeekendUrl = `/race_weekend/${raceWeekend.iracing_session_id}`;

  return (
      <Card
        className="mb-6 transition-all duration-200 hover:shadow-lg hover:border-orange-300/40 hover:cursor-pointer"
        onClick={() => router.visit(raceWeekendUrl)}
      >
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm leading-normal">
              <span className="font-semibold">{userName}</span>
              {timestamp && (
                <span className="text-muted-foreground"> · {timestamp}</span>
              )}
              <span className="text-muted-foreground">
                {" "}
                · Race Weekend ({raceWeekend.sessions.length} sessions)
              </span>
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              <span className="font-medium">{raceWeekend.track.name}</span>
              {" · "}
              <span>
                {raceWeekend.sim_car.manufacturer}{" "}
                {raceWeekend.sim_car.model}
              </span>
            </div>
            {raceWeekend.group.description && (
              <div
                className="leading-normal prose prose-sm max-w-none mt-2"
                dangerouslySetInnerHTML={{
                  __html: raceWeekend.group.description_html || "",
                }}
              />
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Weekend Like/Comment buttons */}
        <div className="flex items-center gap-2 justify-end">
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Button
              onClick={() => setShowCommentInput(!showCommentInput)}
              variant="ghost"
              size="sm"
              className="flex items-center gap-1.5"
            >
              <MessageCircle className="h-5 w-5" />
              {raceWeekend.comments.length > 0 && (
                <span className="text-sm font-medium">
                  {raceWeekend.comments.length}
                </span>
              )}
            </Button>
          </div>
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <RaceWeekendLikeButton
              userId={raceWeekend.user.slug}
              iracingSessionId={raceWeekend.iracing_session_id}
              isLiked={raceWeekend.group.is_liked}
              likesCount={raceWeekend.group.likes_count}
              totalSessions={raceWeekend.sessions.length}
            />
          </div>
        </div>

        {/* Individual Session Cards */}
        <div className="space-y-4 border-l-2 border-muted pl-4">
          {individualEvents.map((event) => (
            <div key={event.id}>
              <TrackEventCard trackEvent={event} />
            </div>
          ))}
        </div>

        {/* Comments Section */}
        {raceWeekend.comments.length > 0 && (
          <div className="pt-4 border-t">
            <CommentsList
              comments={raceWeekend.comments}
              maxComments={3}
              eventUrl={raceWeekendUrl}
            />
          </div>
        )}

        {showCommentInput && (
          <div className="pt-4 border-t">
            <CommentInput
              commentableType="TrackEventGroup"
              commentableId={raceWeekend.group.id}
              onCancel={() => setShowCommentInput(false)}
              autoFocus
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
